(() => {
  'use strict';

  const root = document.documentElement;
  const S = Object.assign(
    {
      smooth: true,
      lerp: 0.1,
      motion: true,
      distance: 'balanced',
      duration: 1.1,
      ease: 'expo',
      split: true,
      parallax: true,
      magnetic: true,
      curtain: false,
      progress: false,
    },
    window.MA_SETTINGS || {}
  );

  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;

  const showAll = () => root.classList.remove('ma-motion');

  if (!gsap || !ScrollTrigger) {
    showAll();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ limitCallbacks: true, ignoreMobileResize: true });
  gsap.defaults({ force3D: true });
  const SplitText = window.SplitText;
  if (SplitText) gsap.registerPlugin(SplitText);

  const EASES = { expo: 'expo.out', quart: 'power4.out', soft: 'power2.out', sine: 'sine.out' };
  const DISTANCES = { short: 24, balanced: 48, long: 90 };
  const ease = EASES[S.ease] || EASES.expo;
  const dist = DISTANCES[S.distance] || DISTANCES.balanced;
  const dur = Math.max(0.3, Number(S.duration) || 1.1);
  const motionOn = !!S.motion && !reduced;

  const contexts = new Map();
  let lenis = null;

  const num = (value, fallback) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  };

  const FROM = {
    'fade-up': () => ({ autoAlpha: 0, y: dist }),
    fade: () => ({ autoAlpha: 0 }),
    'fade-left': () => ({ autoAlpha: 0, x: dist }),
    'fade-right': () => ({ autoAlpha: 0, x: -dist }),
    zoom: () => ({ autoAlpha: 0, scale: 0.92 }),
    blur: () => ({ autoAlpha: 0, y: dist * 0.4, filter: 'blur(12px)' }),
    mask: () => ({ visibility: 'visible', clipPath: 'inset(100% 0% 0% 0%)' }),
    'mask-left': () => ({ visibility: 'visible', clipPath: 'inset(0% 100% 0% 0%)' }),
    'mask-center': () => ({ visibility: 'visible', clipPath: 'inset(12% 12% 12% 12%)' }),
  };
  const END = {
    autoAlpha: 1,
    visibility: 'visible',
    x: 0,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    clipPath: 'inset(0% 0% 0% 0%)',
  };
  const fromVars = (name) => (FROM[name] || FROM['fade-up'])();
  const toVars = (from, extra = {}) => {
    const to = { duration: dur, ease, overwrite: 'auto', ...extra };
    Object.keys(from).forEach((key) => {
      if (key in END) to[key] = END[key];
    });
    to.clearProps = from.filter ? 'filter,transform' : 'transform';
    return to;
  };

  const KEEP_NATIVE =
    '[data-lenis-prevent], .drawer, .menu-drawer, cart-drawer, .predictive-search, .product-media-modal, ' +
    '.popup-modal, quick-add-modal, .mobile-facets, .country-selector, .localization-form, .header__submenu, ' +
    '.mega-menu__content, .quick-order-list__contents';

  function initLenis() {
    if (!S.smooth || reduced || designMode || !window.Lenis) return;
    lenis = new window.Lenis({
      lerp: Math.min(0.25, Math.max(0.06, Number(S.lerp) || 0.1)),
      smoothWheel: true,
      syncTouch: false,
      autoRaf: false,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
      anchors: true,
      prevent: (node) => !!(node && node.closest && node.closest(KEEP_NATIVE)),
    });
    root.classList.add('ma-lenis');
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis && lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    const sync = () => {
      const locked = Array.from(document.body.classList).some((c) => c.indexOf('overflow-hidden') === 0);
      locked ? lenis.stop() : lenis.start();
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  function reveals(scope) {
    scope.querySelectorAll('[data-ma-reveal]').forEach((el) => {
      const from = fromVars(el.getAttribute('data-ma-reveal'));
      gsap.fromTo(
        el,
        from,
        toVars(from, {
          delay: num(el.dataset.maDelay, 0),
          scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        })
      );
    });
  }

  function staggers(scope) {
    scope.querySelectorAll('[data-ma-stagger]').forEach((list) => {
      const items = Array.from(list.querySelectorAll('[data-ma-item]')).filter(
        (item) => item.closest('[data-ma-stagger]') === list
      );
      if (!items.length) return;
      const from = fromVars(list.getAttribute('data-ma-stagger'));
      gsap.set(items, from);
      ScrollTrigger.batch(items, {
        start: 'top 92%',
        once: true,
        onEnter: (batch) => gsap.to(batch, toVars(from, { stagger: 0.08 })),
      });
    });
  }

  function splits(scope) {
    const nodes = scope.querySelectorAll('[data-ma-split]');
    if (!nodes.length) return;
    if (!SplitText || !S.split) {
      nodes.forEach((el) => {
        const from = fromVars('fade-up');
        gsap.fromTo(el, from, toVars(from, { scrollTrigger: { trigger: el, start: 'top 88%', once: true } }));
      });
      return;
    }
    nodes.forEach((el) => {
      const mode = el.getAttribute('data-ma-split') === 'words' ? 'words' : 'lines';
      SplitText.create(el, {
        type: mode === 'words' ? 'words,lines' : 'lines',
        mask: mode,
        autoSplit: true,
        linesClass: 'ma-line',
        wordsClass: 'ma-word',
        aria: 'auto',
        onSplit(self) {
          gsap.set(el, { visibility: 'visible' });
          return gsap.from(mode === 'words' ? self.words : self.lines, {
            yPercent: 110,
            duration: dur * 1.05,
            ease,
            delay: num(el.dataset.maDelay, 0),
            stagger: mode === 'words' ? 0.05 : 0.11,
            scrollTrigger: { trigger: el, start: 'top 90%', once: true },
          });
        },
      });
    });
  }

  function depth(scope) {
    if (!S.parallax) return;
    scope.querySelectorAll('[data-ma-parallax]').forEach((holder) => {
      const speed = Math.min(0.5, Math.max(0.02, num(holder.dataset.maParallax, 0.15)));
      const isMedia = /^(IMG|SVG|VIDEO|PICTURE)$/i.test(holder.tagName);
      const el = isMedia ? holder : holder.querySelector('img, video, svg');
      if (!el) return;
      gsap.set(el, { scale: 1 + speed * 1.2, transformOrigin: '50% 50%' });
      gsap.fromTo(
        el,
        { yPercent: -speed * 50 },
        {
          yPercent: speed * 50,
          ease: 'none',
          scrollTrigger: { trigger: isMedia ? holder.parentElement : holder, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
        }
      );
    });

    scope.querySelectorAll('[data-ma-scale]').forEach((el) => {
      const from = Math.min(1.5, Math.max(1.02, num(el.dataset.maScale, 1.2)));
      gsap.fromTo(
        el,
        { scale: from },
        { scale: 1, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'center center', scrub: 0.6 } }
      );
    });

    gsap.matchMedia().add('(min-width: 990px)', () => {
      scope.querySelectorAll('[data-ma-drift]').forEach((el) => {
        const px = num(el.dataset.maDrift, 60);
        gsap.fromTo(
          el,
          { y: px },
          { y: -px, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.6 } }
        );
      });
    });
  }

  function magnetic(scope, cleanups) {
    if (!finePointer || !motionOn || !S.magnetic) return;
    scope.querySelectorAll('[data-ma-magnetic]').forEach((el) => {
      const strength = num(el.dataset.maMagnetic, 0.3);
      const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'power3.out' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'power3.out' });
      const move = (e) => {
        const r = el.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * strength);
        yTo((e.clientY - (r.top + r.height / 2)) * strength);
      };
      const leave = () => {
        xTo(0);
        yTo(0);
      };
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerleave', leave);
      cleanups.push(() => {
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerleave', leave);
        gsap.set(el, { x: 0, y: 0 });
      });
    });
  }

  const sectionsIn = (scope) =>
    scope && scope.classList && scope.classList.contains('shopify-section')
      ? [scope]
      : Array.from((scope || document).querySelectorAll('.shopify-section'));

  function init(scope = document) {
    sectionsIn(scope).forEach((section) => {
      destroy(section);
      const ctx = gsap.context(() => {
        const cleanups = [];
        if (motionOn) {
          splits(section);
          reveals(section);
          staggers(section);
          depth(section);
        }
        magnetic(section, cleanups);
        return () => cleanups.forEach((fn) => fn());
      }, section);
      contexts.set(section, ctx);
    });
    if (!motionOn) showAll();
  }

  function destroy(scope) {
    sectionsIn(scope).forEach((section) => {
      const ctx = contexts.get(section);
      if (ctx) {
        ctx.revert();
        contexts.delete(section);
      }
    });
  }

  let refreshTimer;
  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (lenis) lenis.resize();
      ScrollTrigger.refresh();
    }, 120);
  }

  function scrollTo(target, opts = {}) {
    if (lenis) return lenis.scrollTo(target, opts);
    if (typeof target === 'number') return window.scrollTo({ top: target, behavior: 'smooth' });
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  function scrollState() {
    let bar = null;
    if (S.progress) {
      bar = document.createElement('div');
      bar.className = 'ma-progress';
      bar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(bar);
    }
    const ring = document.querySelector('.ma-top');
    let scrolled = null;
    ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: (self) => {
        const past = self.scroll() > 24;
        if (past !== scrolled) {
          scrolled = past;
          root.classList.toggle('ma-scrolled', past);
        }
        if (bar) bar.style.transform = 'scaleX(' + self.progress.toFixed(4) + ')';
        if (ring && past) ring.style.setProperty('--ma-page-progress', self.progress.toFixed(3));
      },
    });
  }

  function curtain() {
    if (!S.curtain || designMode || reduced) return;
    window.addEventListener('pageshow', () => root.classList.remove('ma-leaving'));
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if ((a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('data-no-curtain')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      root.classList.add('ma-leaving');
      setTimeout(() => (window.location.href = url.href), 560);
    });
  }

  function themeEditor() {
    if (!designMode) return;
    document.addEventListener('shopify:section:load', (e) => {
      init(e.target);
      refresh();
    });
    document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
    document.addEventListener('shopify:section:reorder', refresh);
    const show = (e) => {
      const section = e.target.closest ? e.target.closest('.shopify-section') : null;
      if (section) section.classList.add('ma-show');
    };
    document.addEventListener('shopify:section:select', show);
    document.addEventListener('shopify:block:select', show);
  }

  function boot() {
    initLenis();
    init(document);
    scrollState();
    curtain();
    themeEditor();
    root.classList.add('ma-ready');
    window.addEventListener('load', refresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);

    const main = document.getElementById('MainContent');
    if (main && 'ResizeObserver' in window) {
      let lastHeight = main.offsetHeight;
      new ResizeObserver(() => {
        const h = main.offsetHeight;
        if (Math.abs(h - lastHeight) > 40) {
          lastHeight = h;
          refresh();
        }
      }).observe(main);
    }

    window.AmbreeMotion = {
      init,
      destroy,
      refresh,
      scrollTo,
      get lenis() {
        return lenis;
      },
      settings: S,
    };
    document.dispatchEvent(new CustomEvent('ma:ready'));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
